/**
 * Collector identity — display name and avatar resolution.
 *
 * The initials fallback was duplicated inline in six files, each with a
 * slightly different name expression, so the same collector could appear as
 * "Ash", "Ash K" or "User" depending on which surface drew them. One resolver
 * keeps a person looking like the same person across the pin, the preview and
 * the sheet.
 *
 * DISCLOSE-1 (b1): that fallback used to be an <img> pointed at the
 * third-party host ui-avatars.com, with the collector's display name in the
 * query string — so that name, and the viewer's IP, reached a processor the
 * privacy policy never named, on every pin, sidebar row and chat row. The owner
 * chose removal over disclosure, so the circle is now drawn locally as an
 * inline SVG `data:` URI: no display name and no request reach
 * ui-avatars.com from anywhere in the app.
 *
 * SCOPE — read this before believing the line above says more than it does.
 * This is the ui-avatars half of DISCLOSE-1 b1. The OTHER half has since
 * landed too: nothing stamps a cdn.pixabay.com default any more
 * ([DISCLOSE-1-MORE-1]), so an account registered without a photo now carries
 * `image.url === ""` and really does reach this circle. `collectorAvatarUrl`
 * below still returns `image.url` AHEAD of it, which is correct — a collector
 * with a real uploaded photo must see the photo.
 *
 * WHAT IS STILL NOT TRUE IS “nothing leaves the device”, and this docblock did
 * once claim it. Two things keep it false. Accounts registered BEFORE that
 * change still hold the pixabay literal — no migration was authorised, and
 * nothing rewrites a stored url on read — so their viewers still reach the CDN.
 * And card art is still hot-linked from images.pokemontcg.io, which the owner
 * split off deliberately: proxying it is the same act as rehosting The Pokémon
 * Company's images and needs an IP answer first. There is no CSP anywhere in
 * the repo either. Do not upgrade this file's comment into “no third party
 * receives anything”, and do not answer the Play Data Safety form from it.
 *
 * `collectorIdentity.localAvatar.test.jsx` holds that line: it walks every
 * non-test `.js/.jsx/.ts/.tsx` file under src/ and fails on the host-and-path
 * literal every historical copy used. That literal is deliberately NOT spelled
 * out here — the scan would flag this very comment, which is how this sentence
 * was first written and how the suite caught it. The exact string lives in the
 * test. Measured as a real gate, not assumed: restoring the old
 * one-liner makes 21 of its 23 tests fail, that scan among them. Know its
 * edges before trusting it as a total ban — it is case-sensitive, it skips
 * `.test.` files and stylesheets, and it cannot see a URL assembled from
 * concatenated pieces.
 *
 * Pure and React-free so markers, sheets and tests can all use it.
 */

const FALLBACK_NAME = "Collector";

/** Full display name, in the order the API actually populates fields. */
export function collectorDisplayName(user) {
  if (!user) return FALLBACK_NAME;
  if (typeof user.username === "string" && user.username.trim()) {
    return user.username.trim();
  }
  const first = user.name?.first || "";
  const last = user.name?.last || "";
  const joined = `${first} ${last}`.trim();
  return joined || FALLBACK_NAME;
}

/** First name only — what a pin label and a one-line greeting should use. */
export function collectorFirstName(user) {
  return collectorDisplayName(user).split(/\s+/)[0] || FALLBACK_NAME;
}

/**
 * Avatar URL with the locally-drawn initials fallback.
 *
 * `image.url` is the field the nearby-collector payload actually carries;
 * `profilePicture` / `profile_picture` are the shapes older mock and profile
 * objects use, so all three are accepted rather than letting one surface show
 * initials while another shows the photo.
 */
export function collectorAvatarUrl(user) {
  const direct =
    user?.image?.url || user?.profilePicture || user?.profile_picture || "";
  if (direct) return direct;
  return generatedAvatarUrl(collectorDisplayName(user));
}

/**
 * Initials for the fallback circle: the first character of the first word plus
 * the first character of the last word, uppercased — "Ash Ketchum" reads "AK",
 * "PokeMaster92" reads "P". Characters are taken with the spread operator, not
 * `charAt`, so a name that starts with an astral-plane character (an emoji, or
 * a script outside the BMP) yields that whole character instead of half a
 * surrogate pair.
 *
 * The first character of a word can be anything the user typed, `<` and `&`
 * included. It is NOT filtered here — `escapeXml` is what makes it safe, so
 * that a name is never silently rewritten into different initials.
 */
export function collectorInitials(name) {
  const safe = String(name ?? "").trim();
  if (!safe) return "";
  const words = safe.split(/\s+/).filter(Boolean);
  const firstOf = (word) => [...word][0] || "";
  const head = firstOf(words[0]);
  const tail = words.length > 1 ? firstOf(words[words.length - 1]) : "";
  return (head + tail).toUpperCase();
}

/**
 * XML entity escaping for text interpolated into the SVG below.
 *
 * A single pass over the input, so `&` becoming `&amp;` is never re-entered
 * and double-escaped. `"` and `'` are covered too: the SVG is
 * assembled as a string, and a stray quote inside an attribute would break out
 * of it just as surely as a stray `<` breaks out of an element.
 *
 * Control characters are DROPPED rather than escaped, because XML 1.0 has no
 * escape for them: a numeric reference to U+0001 is as illegal as the raw
 * byte. Measured against the same XML parser the tests use, a U+0001 inside a
 * <title> is rejected with "disallowed character" — which would turn a
 * character nobody can even see in a display name into a broken avatar. Tab,
 * newline and carriage return are the three XML does allow, and are kept.
 */
const XML_ENTITIES = new Map([
  ["&", "&amp;"],
  ["<", "&lt;"],
  [">", "&gt;"],
  ['"', "&quot;"],
  ["'", "&apos;"],
]);

/**
 * The control characters XML 1.0 forbids outright: U+0000-U+0008, U+000B,
 * U+000C and U+000E-U+001F. Tab, newline and carriage return are legal and are
 * deliberately absent from this set.
 *
 * This set is NOT the whole of what XML forbids, and the gap was a real defect:
 * the production is
 *   Char ::= #x9 | #xA | #xD | [#x20-#xD7FF] | [#xE000-#xFFFD] | [#x10000-#x10FFFF]
 * so U+FFFE and U+FFFF are illegal too. Stopping at 0x1F let them through, and
 * a skeptic measured the result in real headless Chrome: the SVG came back
 * `naturalWidth=0` (broken-image glyph) and jsdom reported "disallowed
 * character" — the exact malformed-document failure this escaping exists to
 * prevent. They are reachable by a self-chosen name: neither
 * `server/users/models/JOI/registerValidation.js` nor `DEFAULT_VALIDATION` in
 * `server/users/models/mongoDB/User.js` restricts the character class, and
 * neither `trim` nor `.toLowerCase()` removes them. They are dropped in
 * `escapeXml` beside the surrogate check rather than added here, because this
 * set is indexed by a code in 0x00-0x1F.
 */
const XML_ILLEGAL_CODES = new Set();
for (let code = 0x00; code <= 0x1f; code += 1) {
  if (code !== 0x09 && code !== 0x0a && code !== 0x0d) {
    XML_ILLEGAL_CODES.add(code);
  }
}

/**
 * True for a character XML cannot carry at all, escaped or not — so the only
 * thing to do with it is drop it.
 *
 * Iterating a string yields whole code points, so an astral character arrives
 * here with `length === 2` and is always legal; a `length === 1` means a
 * genuine BMP character, or an UNPAIRED surrogate.
 *
 * Used by `escapeXml`, which drops these on the way through, and consulted by
 * `drawableInitialsSource` below. It is NOT the whole of the rule for choosing
 * an initial — see that function for the two things this predicate does not
 * know about.
 */
function isXmlUnrepresentable(ch) {
  if (ch.length !== 1) return false;
  const code = ch.charCodeAt(0);
  if (XML_ILLEGAL_CODES.has(code)) return true;
  // A lone surrogate is illegal in XML, and `encodeURIComponent` throws a
  // URIError on one — thrown from a render, that is a blank map rather than a
  // wrong avatar. Dropping it is the cheaper failure.
  if (code >= 0xd800 && code <= 0xdfff) return true;
  // U+FFFE and U+FFFF — see the note above XML_ILLEGAL_CODES.
  return code === 0xfffe || code === 0xffff;
}

/**
 * Characters XML carries happily and a font draws as nothing: zero-width
 * spaces and joiners, the soft hyphen, bidi and other format controls,
 * variation selectors, the combining grapheme joiner.
 *
 * They matter here for one reason only — an initial has to be a letter someone
 * can SEE. Promoting one of these produces a coloured disc with an invisible
 * glyph on it, which is indistinguishable from a bug.
 *
 * Ranges rather than a list because the format classes are contiguous:
 * U+00AD soft hyphen; U+034F CGJ; U+200B-U+200F zero-width space/joiners and
 * the LTR/RTL marks; U+202A-U+202E bidi embedding; U+2060-U+2064 word joiner
 * and invisible operators; U+FE00-U+FE0F variation selectors; U+FEFF BOM.
 * U+FFF9-U+FFFB interlinear annotation. This is not claimed to be every
 * invisible code point in Unicode — it is the set a display name realistically
 * carries, and anything missed degrades to the old behaviour (an invisible
 * initial), not to a crash.
 */
function isInvisible(ch) {
  if (ch.length !== 1) return false;
  const code = ch.charCodeAt(0);
  return (
    code === 0x00ad ||
    code === 0x034f ||
    (code >= 0x200b && code <= 0x200f) ||
    (code >= 0x202a && code <= 0x202e) ||
    (code >= 0x2060 && code <= 0x2064) ||
    (code >= 0xfe00 && code <= 0xfe0f) ||
    code === 0xfeff ||
    (code >= 0xfff9 && code <= 0xfffb)
  );
}

/**
 * The name reduced to the characters an initial may be TAKEN from.
 *
 * Two rules, and a refute round earned both of them by construction:
 *
 * 1. NEVER REMOVE A WORD SEPARATOR. `XML_ILLEGAL_CODES` contains U+000B and
 *    U+000C, and those two are also matched by `/\s/` — so an earlier version of
 *    this function, which removed everything `isXmlUnrepresentable` reported,
 *    MERGED the words on either side of them. `"Ash BrockKetchum"` went
 *    from initials "AK" to "AB": since U+000C is HTML whitespace, every text
 *    surface renders that person as three words while the avatar drew Brock's
 *    initial as their surname's. `escapeXml` still drops these from the
 *    <title>; they simply keep separating words on the way to `collectorInitials`,
 *    which is what `/\s+/` already thinks they do.
 * 2. NEVER PROMOTE AN INVISIBLE CHARACTER. `isXmlUnrepresentable` is not the
 *    same question as "can a font draw this". Skipping only the XML-illegal
 *    characters made `"￾‎"` draw U+200E — a disc with nothing on it —
 *    where the old code at least drew the fallback's visible letter. That is
 *    strictly worse, and it is the "featureless disc with no letter" the comment
 *    this repair replaced had itself warned about.
 *
 * NOT A SANITISER. `<`, `&`, `"` and `'` survive untouched: they are drawable,
 * `escapeXml` is what makes them safe, and rewriting them here would silently
 * change a person's initials — the property `collectorInitials` documents.
 */
function drawableInitialsSource(value) {
  let out = "";
  for (const ch of String(value ?? "")) {
    // Whitespace is a separator, never an initial, so it is never removed —
    // `collectorInitials` splits on it and trims it away by itself.
    if (/\s/.test(ch)) {
      out += ch;
      continue;
    }
    if (isXmlUnrepresentable(ch) || isInvisible(ch)) continue;
    out += ch;
  }
  return out;
}

export function escapeXml(value) {
  let out = "";
  for (const ch of String(value ?? "")) {
    if (isXmlUnrepresentable(ch)) continue;
    out += XML_ENTITIES.get(ch) ?? ch;
  }
  return out;
}

/**
 * Fixed palette for the fallback circle. ui-avatars was called with
 * `background=random`, which re-rolled per request and made the same collector
 * a different colour on the pin and in the sheet; picking from a fixed list by
 * a hash of the name is stable for a given person forever.
 */
const AVATAR_PALETTE = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#dc2626",
  "#ea580c",
  "#0f766e",
  "#15803d",
  "#0891b2",
  "#4f46e5",
  "#b45309",
];

function paletteColor(seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

/**
 * The initials-circle fallback, also used as the <img> onError target.
 *
 * Returns an inline `data:image/svg+xml` URI — a valid `<img src>` that costs
 * no network request at all, so a cold start with no connectivity still draws
 * a face rather than a broken-image glyph.
 *
 * The name is interpolated in two places (the `<title>`, which names the image
 * for anything that reads the SVG on its own, and the initials themselves), and
 * BOTH go through `escapeXml`. Without it a collector called `A <b>&amp; B`
 * would inject markup into the document we are about to hand to an `<img>`.
 * The whole thing is then percent-encoded, which is also what keeps `#` from
 * truncating the URI at a fragment and handles non-ASCII names as UTF-8.
 */
export function generatedAvatarUrl(name) {
  const safe = String(name || FALLBACK_NAME).trim() || FALLBACK_NAME;
  // CHOOSE THE INITIAL FROM WHAT CAN ACTUALLY BE DRAWN, then escape.
  //
  // Escaping first and falling back on the empty result — which is what this
  // did until [DISCLOSE-1-b1-MORE-2] — abandons the collector's whole NAME as
  // soon as its first character is one XML forbids. `\uD800Ash` drew "C", the
  // letter of the generic FALLBACK_NAME, so a real person was labelled with a
  // stranger's initial while the <title> beside it still read "Ash". Same for a
  // leading U+FFFE or U+0001, all of which are storable: neither
  // registerValidation.js nor User.js restricts the character class.
  //
  // Reducing the name first means the NEXT usable character is taken instead —
  // "A" — and the fallback is reached only when a name has no drawable, visible,
  // NON-WHITESPACE character left, which is what it is for. The old code's
  // instinct was right (an initial escaping is about to drop must not count as
  // present); it just gave up on the name rather than reading past the
  // character.
  //
  // BE PRECISE ABOUT THAT CONDITION — a looser wording of it was false and a
  // skeptic constructed the counterexample. It is NOT "no drawable character at
  // all": a name of `" "` keeps a perfectly drawable,
  // XML-legal NBSP and still falls back, because `collectorInitials` trims
  // whitespace before it looks for a word. Same for TAB and U+3000.
  //
  // The palette keys off `safe`, not the reduced value, so a person's colour
  // cannot shift — that one is load-bearing and tested. The <title> also uses
  // `safe`, but do not read anything into it: `escapeXml` drops exactly what
  // `drawableInitialsSource` removes MINUS the invisibles, and the invisibles
  // are unobservable in a title by definition, so the two spellings are
  // indistinguishable there. Measured, not assumed: a mutant building the title
  // from the reduced value leaves all 35 tests green.
  const drawable = drawableInitialsSource(safe);
  const initials =
    escapeXml(collectorInitials(drawable)) ||
    escapeXml(collectorInitials(FALLBACK_NAME));
  const fill = paletteColor(safe);
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" ' +
    'viewBox="0 0 128 128" role="img">' +
    `<title>${escapeXml(safe)}</title>` +
    `<circle cx="64" cy="64" r="64" fill="${fill}"/>` +
    '<text x="64" y="64" dy=".35em" text-anchor="middle" ' +
    'font-family="system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif" ' +
    `font-size="52" font-weight="600" fill="#ffffff">${initials}</text>` +
    "</svg>";
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * True when the collector is online by the app's single definition of it —
 * `isOnline(lastLogin)` in MapPage flattens that to these two fields before a
 * user ever reaches a marker. Never infers activity from anything else.
 */
export function isCollectorOnline(user) {
  return !!(user && (user.online === true || user.onlineStatus === "Online"));
}

/** Public verification, reading the field the server actually sends. */
export function isCollectorVerified(user) {
  return !!(
    user &&
    (user.verified || user.verification?.verified || user.isVerified)
  );
}
